import { useState } from "react";
import { api, fmtDate, fmtBRL } from "@/lib/api";
import { toast } from "sonner";
import { Send, MessageCircle, ChevronDown } from "lucide-react";

const DOT = { delivered: "bg-emerald-400", sent: "bg-sky-400", failed: "bg-red-500", none: "bg-slate-600" };
const TXT = { delivered: "entregue", sent: "enviado", failed: "falhou", none: "não enviado" };

const Bar = ({ label, icon: Icon, s, total, testid }) => (
  <div data-testid={testid}>
    <div className="mb-1 flex items-center justify-between text-xs text-slate-400">
      <span className="flex items-center gap-1.5"><Icon className="h-3.5 w-3.5" /> {label}</span>
      <span className="font-mono text-slate-300">{s.delivered}/{total} entregues{s.read ? ` · ${s.read} lidos` : ""}</span>
    </div>
    <div className="flex h-2.5 overflow-hidden rounded-full bg-[#0F141F]">
      {["delivered", "sent", "failed", "none"].map((k) => s[k] > 0 && <div key={k} className={DOT[k]} style={{ width: `${(100 * s[k]) / Math.max(total, 1)}%` }} title={`${s[k]} ${TXT[k]}`} />)}
    </div>
    <div className="mt-1 flex gap-3 text-[11px] text-slate-500">
      <span>{s.sent} enviados</span><span className={s.failed ? "text-red-400" : ""}>{s.failed} falharam</span><span>{s.none} sem envio</span>
    </div>
  </div>
);

const Chip = ({ ch, st }) => (
  <span title={`${ch === "sms" ? "SMS" : "WhatsApp"}: ${TXT[st.status]}${st.read ? " (lido)" : ""}${st.error ? ` — ${st.error}` : ""}`}
    className="inline-flex items-center gap-1 text-[11px] text-slate-400">
    <span className={`h-2 w-2 rounded-full ${DOT[st.status]} ${st.read ? "ring-2 ring-sky-300/60" : ""}`} />{ch === "sms" ? "SMS" : "WA"}
  </span>
);

export const Row = ({ c, ruleId, onSent }) => {
  const [ask, setAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const already = [c.sms_st.status, c.wa_st.status].some((x) => x === "sent" || x === "delivered");
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/clients/${c.id}/remind`, { channel: "sms_wa", rule_id: ruleId || null });
      const ok = (data.results || []).filter((x) => ["sent", "mock"].includes(x.status)).map((x) => x.channel.toUpperCase());
      const skip = (data.results || []).filter((x) => x.status === "skipped").map((x) => x.error);
      ok.length ? toast.success(`Cobrança enviada: ${ok.join(" + ")}`) : toast.warning(skip[0] || "Nada enviado");
      onSent();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro ao enviar"); }
    setBusy(false); setAsk(false);
  };
  return (
    <div data-testid={`ops-client-${c.id}`} className={`flex items-center gap-3 rounded-lg px-2 py-1.5 ${c.missing ? "bg-red-500/5" : ""}`}>
      <div className="min-w-0 flex-1">
        <div className="truncate text-sm text-slate-200">{c.name || c.panel_user}</div>
        <div className="break-all font-mono text-[11px] leading-tight text-slate-500">{c.panel_user} · {c.account}{c.days_late != null ? ` · venceu há ${c.days_late} ${c.days_late === 1 ? "dia" : "dias"} · ${fmtBRL(c.value)}` : ""}{!c.whatsapp ? " · sem telefone" : ""}</div>
      </div>
      <Chip ch="sms" st={c.sms_st} /><Chip ch="wa" st={c.wa_st} />
      {ask ? (
        <span className="flex items-center gap-1">
          {already && <span data-testid={`ops-send-warn-${c.id}`} className="text-[11px] text-amber-300">já recebeu, reenviar?</span>}
          <button data-testid={`ops-send-confirm-${c.id}`} disabled={busy} onClick={send} className="rounded-md bg-emerald-500 px-2 py-1 text-[11px] font-bold text-slate-950 disabled:opacity-50">{busy ? "..." : "Confirmar"}</button>
          <button data-testid={`ops-send-cancel-${c.id}`} onClick={() => setAsk(false)} className="rounded-md px-2 py-1 text-[11px] text-slate-400 hover:text-white">Cancelar</button>
        </span>
      ) : (
        <button data-testid={`ops-send-${c.id}`} disabled={!c.whatsapp || !c.has_link} onClick={() => setAsk(true)} title={!c.has_link ? "Cliente sem link PIX" : "Enviar SMS + WhatsApp"}
          className="inline-flex items-center gap-1 rounded-md border border-[#1F293D] px-2 py-1 text-[11px] font-semibold text-slate-300 transition-colors hover:border-sky-500/50 hover:text-white disabled:opacity-40"><Send className="h-3 w-3" /> Enviar cobrança</button>
      )}
    </div>
  );
};

export const DayMonitor = ({ id, label, day, onSent }) => {
  const [open, setOpen] = useState(false);
  return (
    <div data-testid={`ops-day-${id}`} className="rounded-2xl border border-[#1F293D] bg-[#111827] p-5 fade-up">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label} · {fmtDate(day.date)}</div>
          <div className="mt-1 font-mono text-3xl font-extrabold text-white">{day.total}<span className="ml-2 text-sm font-normal text-slate-500">vencimentos</span></div>
          <div className="text-[11px] text-slate-500">régua: {day.rule_name || "sem regra ativa"}</div>
        </div>
        <span data-testid={`ops-day-${id}-missing`} className={`rounded-full px-3 py-1 text-xs font-bold ${day.missing ? "bg-red-500/15 text-red-300" : "bg-emerald-500/15 text-emerald-300"}`}>
          {day.missing ? `${day.missing} sem fatura entregue` : "todos receberam"}
        </span>
      </div>
      <div className="mt-4 space-y-3">
        <Bar testid={`ops-day-${id}-sms`} label="SMS" icon={Send} s={day.summary.sms} total={day.total} />
        <Bar testid={`ops-day-${id}-wa`} label="WhatsApp" icon={MessageCircle} s={day.summary.whatsapp} total={day.total} />
      </div>
      <button data-testid={`ops-day-${id}-toggle`} onClick={() => setOpen(!open)} className="mt-4 flex w-full items-center justify-center gap-1 rounded-lg border border-[#1F293D] py-1.5 text-xs text-slate-400 transition-colors hover:text-white">
        {open ? "Esconder clientes" : "Ver clientes"} <ChevronDown className={`h-3.5 w-3.5 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="mt-2 max-h-80 space-y-0.5 overflow-y-auto pr-1">{day.clients.map((c) => <Row key={c.id} c={c} ruleId={day.rule_id} onSent={onSent} />)}</div>}
    </div>
  );
};
